import { z } from "zod";
import { dateOnlySchema, timestampSchema, uuidSchema } from "./api.ts";
import { anyEntityTypeSchema, csvArraySchema, queryBooleanSchema } from "./common.ts";
import { leadStatsSchema } from "./leads.ts";
import { currencyCodeSchema, minorSchema } from "./money.ts";
import { permissionSchema } from "./permissions.ts";

/**
 * REPORTING CONTRACT — the metric dictionary (PRD §7 Item 7).
 *
 * ONE semantic layer. The owner dashboard, every report, every CSV export and
 * (item 14) the AI assistant read the SAME named metrics from
 * `GET /reports/metrics`, so a number can never disagree with itself: there is
 * exactly one definition of "billed revenue" in the product and it lives in
 * `apps/api/src/modules/reports/registry.ts`, described by `metricSchema`.
 *
 * THREE RULES THIS FILE ENCODES
 *  1. **Every metric names its unit.** `money` is minor units plus a currency,
 *     `percent` is BASIS POINTS as an integer (5000 = 50%), `days` is a number
 *     with one decimal, `count` is an integer. No metric is a bare float.
 *  2. **Every metric carries its drill-through.** `drillThrough` says which
 *     list view and which filters show the rows behind the number, and
 *     `GET /reports/metrics/:id/drill` returns exactly those rows with a total
 *     that EQUALS the tile. That equality is asserted in the test suite.
 *  3. **Money is masked, never guessed.** A caller without `financials:view`
 *     gets `value: null` and `masked: true` on every money metric — the count
 *     still comes through, because "12 invoices are overdue" is not a secret.
 */

// ---------------------------------------------------------------------------
// Units, periods and filters
// ---------------------------------------------------------------------------

export const METRIC_UNITS = ["money", "count", "percent", "days"] as const;
export type MetricUnit = (typeof METRIC_UNITS)[number];
export const metricUnitSchema = z.enum(METRIC_UNITS);

/** Period kinds. `fy` is the INDIAN financial year (April–March) by default. */
export const PERIOD_KINDS = ["month", "quarter", "fy", "custom"] as const;
export type PeriodKind = (typeof PERIOD_KINDS)[number];
export const periodKindSchema = z.enum(PERIOD_KINDS);

/**
 * A period, resolved server-side in the workspace timezone (Asia/Kolkata for an
 * Indian agency). `from`/`to` are INCLUSIVE calendar dates, never instants, so
 * "this month" means the same thing in a report, an export and a tile.
 */
export const resolvedPeriodSchema = z.object({
  kind: periodKindSchema,
  from: dateOnlySchema,
  to: dateOnlySchema,
  label: z.string(),
  /** Inclusive day count — the denominator of DSO and of any per-day rate. */
  days: z.number().int(),
  timezone: z.string(),
  /** The comparison window: last month, last quarter, last FY, or the same span before. */
  previousFrom: dateOnlySchema,
  previousTo: dateOnlySchema,
  previousLabel: z.string(),
});
export type ResolvedPeriod = z.infer<typeof resolvedPeriodSchema>;

/** `me` resolves to the caller — what the role dashboards are built on. */
const actorIdSchema = z.union([uuidSchema, z.literal("me")]);

/** The filter vocabulary every metric, report and export understands. */
export const metricFiltersShape = {
  clientId: uuidSchema.optional(),
  projectId: uuidSchema.optional(),
  serviceId: uuidSchema.optional(),
  industryId: uuidSchema.optional(),
  engagementId: uuidSchema.optional(),
  accountManagerId: actorIdSchema.optional(),
  pmId: actorIdSchema.optional(),
  ownerId: actorIdSchema.optional(),
  assigneeId: actorIdSchema.optional(),
  source: z.string().trim().max(80).optional(),
  currency: currencyCodeSchema.optional(),
};
export const metricFiltersSchema = z.object(metricFiltersShape).strict();
export type MetricFilters = z.infer<typeof metricFiltersSchema>;

const periodShape = {
  period: periodKindSchema.optional(),
  /** Anchors `period` — any day inside the wanted month/quarter/FY. Defaults to today. */
  on: dateOnlySchema.optional(),
  from: dateOnlySchema.optional(),
  to: dateOnlySchema.optional(),
};

// ---------------------------------------------------------------------------
// The dictionary
// ---------------------------------------------------------------------------

/**
 * Every metric the product knows. Adding one here and in the API registry is
 * the ONLY way a new number enters a dashboard, a report or the AI's answers.
 */
export const METRIC_IDS = [
  "billed_revenue",
  "credit_notes",
  "collected",
  "tds_credited",
  "outstanding",
  "overdue",
  "dso",
  "mrr",
  "mrr_new",
  "mrr_expansion",
  "mrr_contraction",
  "mrr_churn",
  "pipeline_value",
  "pipeline_weighted",
  "new_leads",
  "won_deals",
  "lost_deals",
  "win_rate",
  "active_clients",
  "new_clients",
  "churned_clients",
  "churned_clients_regretted",
  "projects_at_risk",
  "overdue_tasks",
  "open_follow_ups",
  "revenue_concentration_top5",
  // Profitability and utilisation (PRD US-10.6 / US-10.8) — the arithmetic lives
  // in `time` and `workload`; `reports/registry.ts` registers it (integration pass).
  "margin",
  "margin_pct",
  "worst_margin_clients",
  "effective_hourly_rate",
  "realisation_rate",
  "utilisation",
  "billable_utilisation",
  "budget_burn",
] as const;
export type MetricId = (typeof METRIC_IDS)[number];
export const metricIdSchema = z.enum(METRIC_IDS);

/**
 * Where the rows behind a number live. The dashboard turns this into a link;
 * `GET /reports/metrics/:id/drill` turns it into the rows themselves, and the
 * two always total the same thing.
 */
export const drillThroughSchema = z.object({
  entityType: anyEntityTypeSchema,
  /** Canonical web route of the list view, from ENTITY_ROUTES. */
  path: z.string(),
  /** Query params to pre-apply on that list. Repeated keys are arrays. */
  filters: z.record(z.string(), z.array(z.string())),
  /** The row field that sums to the tile, e.g. "amountMinor". Null for count metrics. */
  totalField: z.string().nullable(),
  label: z.string(),
});
export type DrillThrough = z.infer<typeof drillThroughSchema>;

/** The definition half of a metric — stable, cacheable, and what the AI reads. */
export const metricSchema = z.object({
  id: metricIdSchema,
  label: z.string(),
  description: z.string(),
  unit: metricUnitSchema,
  /** Permission a caller needs at all; null = any active member. */
  permission: permissionSchema.nullable(),
  /** True when the value is money and `financials:view` masks it. */
  financial: z.boolean(),
  /** false when an increase is bad (overdue, churn, DSO) — the UI colours by meaning. */
  goodWhenUp: z.boolean(),
  /** Which period edge the metric describes: a flow over it, or a snapshot at its end. */
  shape: z.enum(["flow", "snapshot"]),
});
export type MetricDefinition = z.infer<typeof metricSchema>;

/** The definition PLUS its value for one period and filter set. */
export const metricValueSchema = metricSchema.extend({
  /** Minor units (money), basis points (percent), whole count, or days with one decimal. */
  value: z.number().nullable(),
  currency: currencyCodeSchema.nullable(),
  /** How many rows are behind the number — never masked. */
  count: z.number().int(),
  previousValue: z.number().nullable(),
  /** Relative change on the previous period, in basis points. Null when there is no base. */
  changeBps: z.number().int().nullable(),
  masked: z.boolean(),
  drillThrough: drillThroughSchema.nullable(),
});
export type MetricValueDto = z.infer<typeof metricValueSchema>;

/** GET /api/v1/reports/metrics (auth) → the batch every dashboard needs in ONE round trip. */
export const metricsQuerySchema = z
  .object({
    ...periodShape,
    ...metricFiltersShape,
    ids: csvArraySchema(metricIdSchema, METRIC_IDS.length).optional(),
    /** Skip the previous-period pass when a caller only wants today's numbers. */
    compare: queryBooleanSchema.optional(),
  })
  .strict();
export type MetricsQuery = z.infer<typeof metricsQuerySchema>;

export const metricsResponseSchema = z.object({
  period: resolvedPeriodSchema,
  currency: currencyCodeSchema,
  metrics: z.array(metricValueSchema),
  generatedAt: timestampSchema,
});
export type MetricsResponseDto = z.infer<typeof metricsResponseSchema>;

/** GET /api/v1/reports/dictionary (auth) → the definitions alone, no numbers. */
export const metricDictionarySchema = z.object({ metrics: z.array(metricSchema) });
export type MetricDictionaryDto = z.infer<typeof metricDictionarySchema>;

// ---------------------------------------------------------------------------
// Drill-through
// ---------------------------------------------------------------------------

/** One row behind a tile. `amountMinor` is what sums to a money metric's value. */
export const drillRowSchema = z.object({
  entityType: anyEntityTypeSchema,
  entityId: uuidSchema,
  /** Canonical record URL, from ENTITY_ROUTES. */
  url: z.string(),
  reference: z.string(),
  title: z.string(),
  subtitle: z.string().nullable(),
  date: dateOnlySchema.nullable(),
  amountMinor: minorSchema.nullable(),
  currency: currencyCodeSchema.nullable(),
  status: z.string().nullable(),
});
export type DrillRowDto = z.infer<typeof drillRowSchema>;

/** GET /api/v1/reports/metrics/:id/drill (auth) → the rows whose total IS the tile. */
export const drillResponseSchema = z.object({
  metric: metricValueSchema,
  target: drillThroughSchema.nullable(),
  rows: z.array(drillRowSchema),
  /** Sum of `amountMinor` over every row — equal to `metric.value` for money metrics. */
  totalMinor: minorSchema.nullable(),
  count: z.number().int(),
  truncated: z.boolean(),
});
export type DrillResponseDto = z.infer<typeof drillResponseSchema>;

export const drillQuerySchema = z
  .object({ ...periodShape, ...metricFiltersShape, limit: z.coerce.number().int().min(1).max(500).optional() })
  .strict();
export type DrillQuery = z.infer<typeof drillQuerySchema>;

// ---------------------------------------------------------------------------
// Dashboards (US-7.1, US-7.4)
// ---------------------------------------------------------------------------

export const DASHBOARD_ROLES = ["owner", "account_manager", "pm", "member", "finance"] as const;
export type DashboardRole = (typeof DASHBOARD_ROLES)[number];
export const dashboardRoleSchema = z.enum(DASHBOARD_ROLES);

export const DASHBOARD_ROLE_LABELS: Record<DashboardRole, string> = {
  owner: "Owner",
  account_manager: "Account manager",
  pm: "Project manager",
  member: "My work",
  finance: "Finance",
};

/** A tile is a metric plus how the dashboard should draw it. */
export const dashboardTileSchema = z.object({
  key: z.string(),
  metric: metricValueSchema,
  /** "stat" | "ageing" | "movement" | "concentration" — picks the tile component. */
  display: z.enum(["stat", "ageing", "movement", "concentration"]),
  /** A second metric shown beside the first (Billed vs Collected, total vs weighted). */
  companion: metricValueSchema.nullable(),
  /** Same metric over the previous financial year, for the FY-to-date tiles. */
  span: z.enum(["period", "fy_to_date"]),
});
export type DashboardTileDto = z.infer<typeof dashboardTileSchema>;

/** One bucket of the ageing bar under the Outstanding tile. */
export const ageingSliceSchema = z.object({
  key: z.string(),
  label: z.string(),
  amountMinor: minorSchema.nullable(),
  count: z.number().int(),
});
export type AgeingSliceDto = z.infer<typeof ageingSliceSchema>;

/** Top-5 revenue concentration: who, how much, what share (US-7.1). */
export const concentrationRowSchema = z.object({
  clientId: uuidSchema,
  clientName: z.string(),
  amountMinor: minorSchema.nullable(),
  shareBps: z.number().int(),
});
export type ConcentrationRowDto = z.infer<typeof concentrationRowSchema>;

export const dashboardResponseSchema = z.object({
  role: dashboardRoleSchema,
  /** The dashboards this caller may switch to, in display order. */
  availableRoles: z.array(dashboardRoleSchema),
  period: resolvedPeriodSchema,
  /** The financial year the FY-to-date tiles cover, e.g. "2026-27". */
  financialYear: z.string(),
  currency: currencyCodeSchema,
  tiles: z.array(dashboardTileSchema),
  ageing: z.array(ageingSliceSchema),
  concentration: z.array(concentrationRowSchema),
  /** Billed and collected month by month across the FY — the hero chart. */
  revenueTrend: z.array(
    z.object({
      month: z.string(),
      label: z.string(),
      billedMinor: minorSchema.nullable(),
      collectedMinor: minorSchema.nullable(),
    }),
  ),
  generatedAt: timestampSchema,
});
export type DashboardResponseDto = z.infer<typeof dashboardResponseSchema>;

export const dashboardQuerySchema = z.object({ ...periodShape, role: dashboardRoleSchema.optional() }).strict();
export type DashboardQuery = z.infer<typeof dashboardQuerySchema>;

// ---------------------------------------------------------------------------
// Reports (US-7.2, US-7.3)
// ---------------------------------------------------------------------------

export const REPORT_IDS = ["revenue", "receivables", "sales", "delivery", "clients"] as const;
export type ReportId = (typeof REPORT_IDS)[number];
export const reportIdSchema = z.enum(REPORT_IDS);

export const REPORT_LABELS: Record<ReportId, string> = {
  revenue: "Revenue",
  receivables: "Receivables",
  sales: "Sales",
  delivery: "Delivery",
  clients: "Clients",
};

/** A generic breakdown row: a dimension value, its money and its count. */
export const reportBucketSchema = z.object({
  key: z.string().nullable(),
  label: z.string(),
  amountMinor: minorSchema.nullable(),
  count: z.number().int(),
  shareBps: z.number().int(),
});
export type ReportBucketDto = z.infer<typeof reportBucketSchema>;

export const revenueMonthSchema = z.object({
  month: z.string(),
  label: z.string(),
  billedMinor: minorSchema.nullable(),
  creditNotesMinor: minorSchema.nullable(),
  collectedMinor: minorSchema.nullable(),
  invoiceCount: z.number().int(),
});
export type RevenueMonthDto = z.infer<typeof revenueMonthSchema>;

/** GET /api/v1/reports/revenue (perm `reports:view`) → US-7.2. */
export const revenueReportSchema = z.object({
  period: resolvedPeriodSchema,
  currency: currencyCodeSchema,
  financialYear: z.string(),
  metrics: z.array(metricValueSchema),
  byMonth: z.array(revenueMonthSchema),
  byClient: z.array(reportBucketSchema),
  byService: z.array(reportBucketSchema),
  byIndustry: z.array(reportBucketSchema),
  byAccountManager: z.array(reportBucketSchema),
  generatedAt: timestampSchema,
});
export type RevenueReportDto = z.infer<typeof revenueReportSchema>;

/** GET /api/v1/reports/sales (perm `reports:view`) → US-7.3, on top of the leads module. */
export const salesReportSchema = z.object({
  period: resolvedPeriodSchema,
  currency: currencyCodeSchema,
  metrics: z.array(metricValueSchema),
  /** Pipeline, funnel, cycle time, speed-to-lead and lost reasons, from `leads`. */
  breakdown: leadStatsSchema,
  generatedAt: timestampSchema,
});
export type SalesReportDto = z.infer<typeof salesReportSchema>;

export const deliveryProjectRowSchema = z.object({
  projectId: uuidSchema,
  number: z.string(),
  name: z.string(),
  clientId: uuidSchema.nullable(),
  clientName: z.string().nullable(),
  status: z.string(),
  health: z.string(),
  endDate: dateOnlySchema.nullable(),
  openTasks: z.number().int(),
  overdueTasks: z.number().int(),
});
export type DeliveryProjectRowDto = z.infer<typeof deliveryProjectRowSchema>;

export const quotaFulfilmentRowSchema = z.object({
  engagementId: uuidSchema,
  clientId: uuidSchema.nullable(),
  clientName: z.string().nullable(),
  deliverableType: z.string(),
  period: z.string(),
  committed: z.number().int(),
  delivered: z.number().int(),
  fulfilmentBps: z.number().int(),
});
export type QuotaFulfilmentRowDto = z.infer<typeof quotaFulfilmentRowSchema>;

/** GET /api/v1/reports/delivery (perm `reports:view`) → US-7.3. */
export const deliveryReportSchema = z.object({
  period: resolvedPeriodSchema,
  metrics: z.array(metricValueSchema),
  byStatus: z.array(reportBucketSchema),
  byHealth: z.array(reportBucketSchema),
  atRisk: z.array(deliveryProjectRowSchema),
  overdueTasksByPerson: z.array(reportBucketSchema),
  overdueTasksByClient: z.array(reportBucketSchema),
  quotaFulfilment: z.array(quotaFulfilmentRowSchema),
  generatedAt: timestampSchema,
});
export type DeliveryReportDto = z.infer<typeof deliveryReportSchema>;

export const clientMovementRowSchema = z.object({
  clientId: uuidSchema,
  number: z.string(),
  name: z.string(),
  status: z.string(),
  accountManagerName: z.string().nullable(),
  industryName: z.string().nullable(),
  date: dateOnlySchema.nullable(),
  /** Churn only: true = we wanted to keep them, false = a neutral parting. */
  regretted: z.boolean().nullable(),
  reasonCode: z.string().nullable(),
  mrrMinor: minorSchema.nullable(),
});
export type ClientMovementRowDto = z.infer<typeof clientMovementRowSchema>;

/** GET /api/v1/reports/clients (perm `reports:view`) → US-7.3. */
export const clientsReportSchema = z.object({
  period: resolvedPeriodSchema,
  currency: currencyCodeSchema,
  metrics: z.array(metricValueSchema),
  newClients: z.array(clientMovementRowSchema),
  churned: z.array(clientMovementRowSchema),
  churnReasons: z.array(reportBucketSchema),
  byIndustry: z.array(reportBucketSchema),
  byAccountManager: z.array(reportBucketSchema),
  generatedAt: timestampSchema,
});
export type ClientsReportDto = z.infer<typeof clientsReportSchema>;

export const reportQuerySchema = z.object({ ...periodShape, ...metricFiltersShape }).strict();
export type ReportQuery = z.infer<typeof reportQuerySchema>;

/** GET /api/v1/reports/:id/export.csv (perm `reports:view` + `data:export`). */
export const reportExportParamsSchema = z.object({ id: reportIdSchema });
export type ReportExportParams = z.infer<typeof reportExportParamsSchema>;

export const reportExportQuerySchema = z
  .object({ ...periodShape, ...metricFiltersShape, section: z.string().trim().max(40).optional() })
  .strict();
export type ReportExportQuery = z.infer<typeof reportExportQuerySchema>;
